/**
 * Service for Brickset authentication and credentials management.
 * Handles user hash generation and credential storage.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

/**
 * Get a Brickset user hash by authenticating with username and password.
 * Calls our backend which proxies the request to Brickset API to avoid CORS issues.
 * The password is sent to our backend which immediately forwards it to Brickset and returns the hash.
 * The password is never stored on our servers.
 *
 * @param username Brickset username
 * @param password Brickset password
 * @param apiKey Brickset API key (required for authentication)
 * @returns User hash from Brickset
 * @throws Error if authentication fails
 */
export async function getUserHash(
  username: string,
  password: string,
  apiKey: string
): Promise<string> {
  // Call our backend proxy which handles CORS
  const response = await fetch(`${API_BASE_URL}/api/brickset/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      apiKey: apiKey,
      username: username,
      password: password
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 401) {
      throw new Error(errorData.error || 'Invalid username or password. Please verify your credentials at https://brickset.com');
    }
    throw new Error(errorData.error || 'Failed to obtain user hash from Brickset');
  }

  const data = await response.json();

  // Check if response has an error
  if (data.error) {
    throw new Error(data.error);
  }

  // Return the hash from the response
  if (!data.hash) {
    throw new Error('No user hash returned from Brickset');
  }

  return data.hash;
}

/**
 * Save Brickset credentials (API key and user hash) to user settings.
 *
 * @param apiKey Brickset API key
 * @param userHash Brickset user hash
 * @param token JWT token for authentication
 * @throws Error if save fails
 */
export async function saveCredentials(
  apiKey: string,
  userHash: string,
  token: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/user-settings`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      settings: {
        brickset_api_key: apiKey,
        brickset_user_hash: userHash
      }
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to save credentials');
  }
}

/**
 * Check if the user already has Brickset credentials saved.
 *
 * @param token JWT token for authentication
 * @returns True if user has both API key and user hash saved
 * @throws Error if check fails
 */
export async function checkExistingCredentials(token: string): Promise<{
  hasApiKey: boolean;
  hasUserHash: boolean;
  hasBoth: boolean;
}> {
  const response = await fetch(`${API_BASE_URL}/api/user-settings`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to check existing credentials');
  }

  const data = await response.json();
  const settings = data.settings || [];

  const apiKeySetting = settings.find((s: { key: string; value: string }) => s.key === 'brickset_api_key');
  const userHashSetting = settings.find((s: { key: string; value: string }) => s.key === 'brickset_user_hash');

  const hasApiKey = apiKeySetting && apiKeySetting.value && apiKeySetting.value !== '';
  const hasUserHash = userHashSetting && userHashSetting.value && userHashSetting.value !== '';

  return {
    hasApiKey,
    hasUserHash,
    hasBoth: hasApiKey && hasUserHash
  };
}

/**
 * Delete Brickset credentials from user settings.
 *
 * @param token JWT token for authentication
 * @throws Error if delete fails
 */
export async function deleteCredentials(token: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/user-settings`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      keys: ['brickset_api_key', 'brickset_user_hash']
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete credentials');
  }
}
